import { FC, useState } from 'react';
import { useOne, useNavigation, useTranslate, useUpdate, useNotification } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Button, Card, ImageViewer, Tag, Dialog, TextInput } from '@scalingworks/react-admin-ui';
import { Loading, SomethingWentWrong, ShowPageWrapper, ImageGallery } from '~/components';
import { resourceNames } from '~/resources/resource-names';
import { ChallengeSubmission, ChallengeSubmissionStatus } from '~/api';
import { ChallengeStatusColor, challengeSubmissionFields } from '../challenge-submission.resource';
import { formatFullName } from '~/resources/helpers';
import dayjs from 'dayjs';
import { FullDateTimeFormat } from '~/config/constant';
import { LinkToDetails, sentenceCase } from '@scalingworks/refine-react-admin';

export const ChallengeSubmissionShowPage: FC = () => {
  // ======================= HOOKS
  const { id } = useParams();
  const navigation = useNavigation();
  const t = useTranslate();
  const notification = useNotification();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [adminReason, setAdminReason] = useState('');
  const [internalRemark, setInternalRemark] = useState('');

  if (!id) return <SomethingWentWrong />;

  // ======================= API
  const { data, isLoading, refetch } = useOne<ChallengeSubmission>({
    resource: resourceNames.challengeSubmission,
    id,

    meta: {
      fields: challengeSubmissionFields,
    },
  });

  const { mutate: updateSubmission, isLoading: isUpdating } = useUpdate();

  if (isLoading) return <Loading />;

  const { data: submission } = data || {};
  if (!submission) return <SomethingWentWrong />;

  // Extract submission details
  const {
    createdAt,
    status,
    approvedDate,
    rejectedDate,
    adminReason: existingAdminReason,
    internalRemark: existingInternalRemark,
    imageUrls,
    challenge,
    customer,
    admin,
    metadata,
  } = submission;

  const isPending = status === ChallengeSubmissionStatus.Pending;
  const submittedAt = dayjs(createdAt).format(FullDateTimeFormat);
  const customerName = formatFullName(customer?.firstName, customer?.lastName);
  const adminName = formatFullName(admin?.firstName, admin?.lastName);

  // ======================= EVENTS
  const handleApprove = () => {
    updateSubmission(
      {
        resource: resourceNames.challengeSubmission,
        id,
        values: {
          status: ChallengeSubmissionStatus.Approved,
          adminReason,
          internalRemark,
        },
        successNotification: {
          message: t('challengeSubmissions.approve.success', {
            fallback: 'Submission approved successfully',
          }),
          type: 'success',
        },
        errorNotification: {
          message: t('challengeSubmissions.approve.error', {
            fallback: 'Failed to approve submission',
          }),
          type: 'error',
        },
      },
      {
        onSuccess: () => {
          setIsApproveModalOpen(false);
          refetch();
        },
      }
    );
  };

  const handleReject = () => {
    if (!adminReason.trim()) {
      notification?.open?.({
        message: t('challengeSubmissions.reject.reasonRequired', {
          fallback: 'Please provide a reason for rejection',
        }),
        type: 'error',
      });
      return;
    }

    updateSubmission(
      {
        resource: resourceNames.challengeSubmission,
        id,
        values: {
          status: ChallengeSubmissionStatus.Rejected,
          adminReason,
          internalRemark,
        },
        successNotification: {
          message: t('challengeSubmissions.reject.success', {
            fallback: 'Submission rejected successfully',
          }),
          type: 'success',
        },
        errorNotification: {
          message: t('challengeSubmissions.reject.error', {
            fallback: 'Failed to reject submission',
          }),
          type: 'error',
        },
      },
      {
        onSuccess: () => {
          setIsRejectModalOpen(false);
          refetch();
        },
      }
    );
  };

  // Render metadata section
  const renderMetadataSection = () => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return null;
    }

    return (
      <div className="col-span-2 mt-4">
        <h3 className="font-semibold mb-2">
          {t('challengeSubmissions.show.metadata', {
            fallback: 'Submission Metadata',
            ns: 'common',
          })}
          :
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
          {Object.entries(metadata).map(([key, value]) => (
            <div key={key}>
              <h4 className="font-medium text-sm text-gray-600 mb-1">{sentenceCase(key)}:</h4>
              <p>{typeof value === 'string' ? value : JSON.stringify(value)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <ShowPageWrapper
      resourceName={resourceNames.challengeSubmission}
      title={t('challengeSubmissions.show.title', {
        fallback: 'Review Submission Details',
        ns: 'common',
      })}
      extra={
        isPending && (
          <div className="flex flex-row gap-2">
            <Button
              variant="solid"
              color="success"
              onClick={() => setIsApproveModalOpen(true)}
              disabled={isUpdating}
            >
              {t('actions.approve', { fallback: 'Approve' })}
            </Button>

            <Button
              variant="solid"
              color="danger"
              onClick={() => setIsRejectModalOpen(true)}
              disabled={isUpdating}
            >
              {t('actions.reject', { fallback: 'Reject' })}
            </Button>
          </div>
        )
      }
    >
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left column - Submission Details */}
        <Card className="p-6 md:col-span-2">
          <h2 className="text-xl font-semibold mb-4">
            {t('challengeSubmissions.show.submissionDetails', {
              fallback: 'Submission Details',
              ns: 'common',
            })}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
            <div>
              <h3 className="font-semibold mb-1">
                {t('challengeSubmissions.show.customer', {
                  fallback: 'Customer',
                  ns: 'common',
                })}
                :
              </h3>
              <LinkToDetails
                resourceId={customer?.id || ''}
                resourceName={resourceNames.customer}
                highlight
              >
                {customerName}
              </LinkToDetails>
              <p className="text-sm text-gray-600">{customer?.phoneNumber}</p>
            </div>

            {admin && (
              <div>
                <h3 className="font-semibold mb-1">
                  {t('challengeSubmissions.show.admin', {
                    fallback: 'Admin',
                    ns: 'common',
                  })}
                  :
                </h3>
                <p>{adminName}</p>
              </div>
            )}

            <div>
              <h3 className="font-semibold mb-1">
                {t('challengeSubmissions.show.submittedAt', {
                  fallback: 'Submitted At',
                  ns: 'common',
                })}
                :
              </h3>
              <p>{submittedAt}</p>
            </div>

            <div>
              <h3 className="font-semibold mb-1">
                {t('challengeSubmissions.show.status', {
                  fallback: 'Status',
                  ns: 'common',
                })}
                :
              </h3>
              <Tag color={ChallengeStatusColor[status] || 'default'}>
                {t(`challengeSubmissions.status.${status?.toLowerCase() || 'unknown'}`, {
                  fallback: status || 'Unknown',
                  ns: 'common',
                }).toUpperCase()}
              </Tag>
            </div>

            <div>
              <h3 className="font-semibold mb-1">
                {t('challengeSubmissions.show.challenge', {
                  fallback: 'Challenge',
                  ns: 'common',
                })}
                :
              </h3>
              <LinkToDetails
                resourceId={challenge.id}
                resourceName={resourceNames.challenge}
                highlight
              >
                {challenge.title || '-'}
              </LinkToDetails>
            </div>

            {status === ChallengeSubmissionStatus.Approved && approvedDate && (
              <div>
                <h3 className="font-semibold mb-1">
                  {t('challengeSubmissions.show.approvedDate', {
                    fallback: 'Approved Date',
                    ns: 'common',
                  })}
                  :
                </h3>
                <p>{dayjs(approvedDate).format(FullDateTimeFormat)}</p>
              </div>
            )}

            {status === ChallengeSubmissionStatus.Rejected && rejectedDate && (
              <div>
                <h3 className="font-semibold mb-1">
                  {t('challengeSubmissions.show.rejectedDate', {
                    fallback: 'Rejected Date',
                    ns: 'common',
                  })}
                  :
                </h3>
                <p>{dayjs(rejectedDate).format(FullDateTimeFormat)}</p>
              </div>
            )}

            {(status === ChallengeSubmissionStatus.Rejected ||
              status === ChallengeSubmissionStatus.Approved) &&
              existingAdminReason && (
                <div className="col-span-2">
                  <h3 className="font-semibold mb-1">
                    {t('challengeSubmissions.show.adminReason', {
                      fallback: 'Admin Reason',
                      ns: 'common',
                    })}
                    :
                  </h3>
                  <p>{existingAdminReason}</p>
                </div>
              )}

            {existingInternalRemark && (
              <div className="col-span-2">
                <h3 className="font-semibold mb-1">
                  {t('challengeSubmissions.show.internalRemark', {
                    fallback: 'Internal Remark',
                    ns: 'common',
                  })}
                  :
                </h3>
                <p>{existingInternalRemark}</p>
              </div>
            )}

            {renderMetadataSection()}
          </div>
        </Card>

        {/* Right column - Submission Images */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            {t('challengeSubmissions.show.submissionImages', {
              fallback: 'Submission Images',
              ns: 'common',
            })}
          </h2>

          {imageUrls && imageUrls.length > 0 ? (
            <ImageGallery images={imageUrls} />
          ) : (
            <p className="text-gray-500">
              {t('challengeSubmissions.show.noImages', {
                fallback: 'No images submitted',
                ns: 'common',
              })}
            </p>
          )}
        </Card>
      </div>

      {/* Approve Modal */}
      <Dialog.Root open={isApproveModalOpen} onOpenChange={setIsApproveModalOpen}>
        <Dialog.Overlay />
        <Dialog.Content>
          <Dialog.Title>
            {t('challengeSubmissions.approve.title', {
              fallback: 'Approve Challenge Submission',
              ns: 'common',
            })}
          </Dialog.Title>
          <Dialog.CloseButton />

          <div className="py-4">
            <p className="mb-4">
              {t('challengeSubmissions.approve.confirmation', {
                fallback: 'Are you sure you want to approve this challenge submission?',
                ns: 'common',
              })}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('challengeSubmissions.approve.adminReason', {
                    fallback: 'Admin Reason (Optional)',
                    ns: 'common',
                  })}
                  :
                </label>
                <TextInput
                  type="textarea"
                  value={adminReason}
                  onValue={setAdminReason}
                  placeholder={t('challengeSubmissions.approve.adminReasonPlaceholder', {
                    fallback: 'Enter admin reason...',
                    ns: 'common',
                  })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('challengeSubmissions.approve.internalRemark', {
                    fallback: 'Internal Remark (Optional)',
                    ns: 'common',
                  })}
                  :
                </label>
                <TextInput
                  type="textarea"
                  value={internalRemark}
                  onValue={setInternalRemark}
                  placeholder={t('challengeSubmissions.approve.internalRemarkPlaceholder', {
                    fallback: 'Enter optional remark...',
                    ns: 'common',
                  })}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              variant="solid"
              onClick={() => setIsApproveModalOpen(false)}
              disabled={isUpdating}
            >
              {t('actions.cancel', { fallback: 'Cancel' })}
            </Button>
            <Button variant="solid" color="success" onClick={handleApprove} loading={isUpdating}>
              {t('actions.approve', { fallback: 'Approve' })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      {/* Reject Modal */}
      <Dialog.Root open={isRejectModalOpen} onOpenChange={setIsRejectModalOpen}>
        <Dialog.Overlay />
        <Dialog.Content>
          <Dialog.Title>
            {t('challengeSubmissions.reject.title', {
              fallback: 'Reject Challenge Submission',
              ns: 'common',
            })}
          </Dialog.Title>
          <Dialog.CloseButton />

          <div className="py-4">
            <p className="mb-4">
              {t('challengeSubmissions.reject.confirmation', {
                fallback: 'Please provide a reason for rejecting this submission',
                ns: 'common',
              })}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('challengeSubmissions.reject.adminReason', {
                    fallback: 'Admin Reason (Required)',
                    ns: 'common',
                  })}
                  :
                </label>
                <TextInput
                  type="textarea"
                  value={adminReason}
                  onValue={setAdminReason}
                  placeholder={t('challengeSubmissions.reject.adminReasonPlaceholder', {
                    fallback: 'Enter reason for rejection...',
                    ns: 'common',
                  })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('challengeSubmissions.reject.internalRemark', {
                    fallback: 'Internal Remark (Optional)',
                    ns: 'common',
                  })}
                  :
                </label>
                <TextInput
                  type="textarea"
                  value={internalRemark}
                  onValue={setInternalRemark}
                  placeholder={t('challengeSubmissions.reject.internalRemarkPlaceholder', {
                    fallback: 'Enter optional internal remark...',
                    ns: 'common',
                  })}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              variant="solid"
              onClick={() => setIsRejectModalOpen(false)}
              disabled={isUpdating}
            >
              {t('actions.cancel', { fallback: 'Cancel' })}
            </Button>
            <Button variant="solid" color="danger" onClick={handleReject} loading={isUpdating}>
              {t('actions.reject', { fallback: 'Reject' })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </ShowPageWrapper>
  );
};
